import axios from "axios";
import {
  printData,
  saveLocalStorage,
  dataLocalStorage,
  getLocalStorage,
  removeAllLocalStorage,
} from "src/helper/publicFunction.jsx";
import { handleUnauthorized } from "src/common/authHandler.js";

const axiosServices = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? "http://localhost:8080",
  timeout: 20000, // Set a timeout (in milliseconds) for requests
  withCredentials: true, // CRITICAL: Must be true for CORS with credentials
  headers: {
    "Content-Type": "application/json",
    // You can add any other common headers here
  },
});

// Flag to prevent multiple refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// interceptor for http
// Request Interceptor: Handle request errors or add common request headers
axiosServices.interceptors.request.use(
  (config) => {
    // Add token to headers
    const userData = getLocalStorage(dataLocalStorage.userinfo);
    const accessToken = userData?.token;

    if (accessToken) {
      config.headers = {
        ...config.headers,
        token: accessToken,
      };
      console.log("🚀 Request interceptor - Token:", {
        token: accessToken.substring(0, 20) + "...",
        url: config.url,
        fullUrl: `${config.baseURL}${config.url}`,
      });
    } else {
      console.log("🚀 Request interceptor - No token available");
    }

    // Debug log
    console.log("🚀 Sending request:", {
      method: config.method?.toUpperCase(),
      url: config.url,
      fullUrl: `${config.baseURL}${config.url}`,
      withCredentials: config.withCredentials,
      headers: config.headers,
    });

    return config;
  },
  (error) => {
    // Handle request errors here
    return Promise.reject(error);
  },
);

// Response Interceptor: Handle response errors

axiosServices.interceptors.response.use(
  (response) => {
    try {
      const data = response?.data || {};
      const privileges =
        data?.results?.dataPrivileges ||
        data?.dataPrivileges ||
        data?.data?.dataPrivileges ||
        data?.privileges;
      if (privileges) {
        try {
          saveLocalStorage(dataLocalStorage.privileges, privileges);
        } catch (e) {
          // ignore
        }
      }

      const user =
        data?.results?.admin ||
        data?.data?.user ||
        data?.user ||
        data?.results?.user ||
        data?.data;
      if (user && user?.token) {
        try {
          saveLocalStorage(dataLocalStorage.userinfo, user);
        } catch (e) {
          // ignore
        }
      }
    } catch (e) {
      // ignore
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const requestUrl = originalRequest?.url || "";

    if (status === 401) {
      console.log("401 error detected for URL:", requestUrl);
    }

    const isLoginRequest = requestUrl.includes("/api/v1/auth/logIn");
    const isRefreshRequest = requestUrl.includes("/api/v1/auth/refreshToken");

    // If the refresh token request itself returns 401 (or any failure caught here),
    // treat it as an invalid session and force logout + redirect.
    if (status === 401 && isRefreshRequest) {
      console.error("Refresh token request returned 401 – forcing logout.");
      try {
        await axiosServices.post("/api/v1/auth/logout");
      } catch (logoutError) {
        // ignore logout errors
      }

      removeAllLocalStorage();
      window.location.href = "/auth/login";

      return Promise.reject(error);
    }

    if (status === 401 && !originalRequest._retry) {
      // Don't attempt refresh if the request is to the login endpoint
      if (isLoginRequest) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["token"] = token;
            return axiosServices(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log("Attempting token refresh...");

        // Try to get refresh token from localStorage first, then use cookies
        const refreshToken = localStorage.getItem("refreshToken");

        let response;
        if (refreshToken) {
          // If we have refresh token in localStorage, send it in the request body
          response = await axiosServices.post("/api/v1/auth/refreshToken", {
            refreshToken: refreshToken,
          });
        } else {
          // Otherwise, rely on cookies (HTTP-only cookie)
          response = await axiosServices.post("/api/v1/auth/refreshToken", {});
        }

        console.log("Refresh response:", response);

        // Normalise payload so it works for both:
        // 1) { token, user, ... }
        // 2) { data: { token, user, ... } }
        const rawData = response?.data || {};
        const payload =
          rawData?.data &&
          (rawData.data.token ||
            rawData.data.accessToken ||
            rawData.data.user ||
            rawData.data.admin)
            ? rawData.data
            : rawData;

        const newUser = payload;

        // Support both `token` and `accessToken` from backend
        const newToken = payload?.token || payload?.accessToken;

        if (newToken) {
          console.log("Token refresh successful");

          // Get existing user info to preserve firstName, lastName, email, etc.
          const existingUserInfo =
            getLocalStorage(dataLocalStorage.userinfo) || {};

          // Check if response includes user data in different structures
          const userFromResponse =
            payload?.user || payload?.admin || rawData?.user || rawData?.admin;

          // Merge: existing user info (preserves firstName, lastName) + response user data
          // Token is set LAST to ensure it's not overwritten
          const updatedUser = {
            ...existingUserInfo,
            ...(userFromResponse || {}), // Use user data from response if available
            ...newUser, // Include all data from newUser (flatten)
            // Preserve critical fields if they exist in existing user info
            firstName:
              userFromResponse?.firstName ||
              newUser?.firstName ||
              existingUserInfo.firstName,
            lastName:
              userFromResponse?.lastName ||
              newUser?.lastName ||
              existingUserInfo.lastName,
            email:
              userFromResponse?.email ||
              newUser?.email ||
              existingUserInfo.email,
            uuid:
              userFromResponse?.uuid || newUser?.uuid || existingUserInfo.uuid,
            // Set token LAST to ensure it's always the refreshed token
            token: newToken,
          };

          // Avoid storing nested user/admin objects inside the user object
          if (updatedUser.user) {
            delete updatedUser.user;
          }
          if (updatedUser.admin) {
            delete updatedUser.admin;
          }

          console.log("Saving refreshed token to localStorage:", {
            token: newToken.substring(0, 20) + "...",
          });
          saveLocalStorage(dataLocalStorage.userinfo, updatedUser);

          // Save new refresh token if provided (support multiple shapes)
          const newRefreshToken =
            payload?.refreshToken ||
            rawData?.refreshToken ||
            rawData?.data?.refreshToken;
          if (newRefreshToken) {
            console.log("Saving new refresh token");
            localStorage.setItem("refreshToken", newRefreshToken);
          }

          // Update axios default headers with new token
          axiosServices.defaults.headers.common["token"] = newToken;
          originalRequest.headers["token"] = newToken;

          console.log("Token refresh successful, retrying original request");
          processQueue(null, newToken);
          return axiosServices(originalRequest);
        } else {
          console.log("No token in refresh response");
          throw new Error("No token in refresh response");
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        console.error("Refresh error response:", refreshError.response);
        processQueue(refreshError, null);
        // Don't force logout - let the user continue using the app
        // The refresh will be attempted again on the next 401 error
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For other errors, use the original handler
    if (error.response?.status !== 401) {
      handleUnauthorized(error);
    }

    // Handle response errors here
    if (error.response) {
      // The server responded with an error
      printData({
        name: "Server Error:",
        data: error?.response?.data,
        type: "error",
      });
      return Promise.reject(error);
    } else if (error.request) {
      // The request was made, but there was no response from the server
      printData({
        name: "Network Error:",
        data: error?.message,
        type: "error",
      });
      return Promise.reject(
        new Error("Network error, please try again later."),
      );
    } else {
      // Something else caused the request to fail
      printData({
        name: "Request Error:",
        data: error?.message,
        type: "error",
      });
      return Promise.reject(
        new Error("An error occurred while making the request."),
      );
    }
  },
);
export default axiosServices;
